import { useParams } from "react-router-dom";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Divider,
  TextField,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Card,
  CardContent,
  CardActions,
  Chip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import PersonIcon from "@mui/icons-material/Person";
import HomeIcon from "@mui/icons-material/Home";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import StepTitle from "../../components/Checkout/StepTitle";
import usePerfilPU from "../../services/usePerfilPU";
import dayjs from "dayjs";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useUpdatePerfilPU } from "../../services/usePerfilPU";
import { useState } from "react";
import type { PUProfile } from "../../services/usePerfilPU";
import OrderDetailsModal from "../../components/OrderDetailsModal";
import type { Order } from "../../components/OrderDetailsModal";
import AddressFieldset from "../../components/AddressFieldset";
import AllOrdersModal from "../../components/AllOrdersModal";
import CreditTransactionsCard from "./CreditTransactionsCard";
import useAllOrders from "../../services/useAllOrders";
import { getBestAddress } from "../../utils/addressUtils";
import validatePhone from "../../utils/validatePhone";
import phoneToE164 from "../../utils/phoneToE164";
import validateEmail from "../../utils/validateEmail";

const ORDER_STATUS_MAP = {
  ORDERED: "Pedido",
  PAID: "Pago",
  CANCELED: "Cancelado",
  FINISHED: "Finalizado",
};

const ORDER_STATUS_COLORS = {
  ORDERED: "info",
  PAID: "warning",
  CANCELED: "error",
  FINISHED: "success",
} as const;

// Componente de card para pedidos no mobile
const OrderCard = ({
  order,
  onViewDetails,
}: {
  order: Order;
  onViewDetails: (order: Order) => void;
}) => {
  return (
    <Card
      variant="outlined"
      sx={{
        mb: 2,
        borderRadius: 2,
        border: "1px solid #e0e0e0",
        boxShadow: "none",
        "&:hover": {
          borderColor: "#bdbdbd",
        },
      }}
    >
      <CardContent sx={{ pb: 1 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6" component="div" color="primary.main">
            Pedido #{order.id}
          </Typography>
          <Chip
            label={ORDER_STATUS_MAP[order.status]}
            color={
              ORDER_STATUS_COLORS[
                order.status as keyof typeof ORDER_STATUS_COLORS
              ]
            }
            size="small"
            sx={{ fontWeight: 600 }}
          />
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <LocationOnIcon
              sx={{ color: "text.secondary", mr: 1, fontSize: 18 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
              {order.address}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center" }}>
            <ShoppingCartIcon
              sx={{ color: "text.secondary", mr: 1, fontSize: 18 }}
            />
            <Typography variant="body2" color="text.secondary">
              {order.items_count} {order.items_count === 1 ? "item" : "itens"}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center" }}>
            <CalendarTodayIcon
              sx={{ color: "text.secondary", mr: 1, fontSize: 18 }}
            />
            <Typography variant="body2" color="text.secondary">
              {dayjs(order.created_at).format("DD/MM/YYYY")}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center" }}>
            <AttachMoneyIcon
              sx={{ color: "text.secondary", mr: 1, fontSize: 18 }}
            />
            <Typography variant="body1" fontWeight={600} color="text.primary">
              R$ {order.total.toFixed(2)}
            </Typography>
          </Box>
        </Box>
      </CardContent>

      <CardActions sx={{ pt: 0, px: 2, pb: 2 }}>
        <Button
          size="small"
          variant="outlined"
          onClick={() => onViewDetails(order)}
          fullWidth
          sx={{ borderRadius: 2 }}
        >
          Ver detalhes
        </Button>
      </CardActions>
    </Card>
  );
};

const Perfil = () => {
  const { slug } = useParams();
  const { data: profile, isLoading } = usePerfilPU();
  const { data: orders } = useAllOrders({
    seedSlug: slug,
    page: 1,
    pageSize: 5,
  });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [editOpen, setEditOpen] = useState(false);
  const [passwordChanged, setPasswordChanged] = useState(false);
  const [orderDetailsOpen, setOrderDetailsOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [allOrdersOpen, setAllOrdersOpen] = useState(false);
  const {
    mutate,
    isLoading: isSaving,
    isSuccess,
    isError,
    error: updateError,
  } = useUpdatePerfilPU();

  // Estado para erros de validação específicos
  const [emailError, setEmailError] = useState<string | null>(null);

  // Placeholders para dados enquanto carrega
  const nome = isLoading ? "..." : profile?.name || "-";
  const endereco = isLoading
    ? "..."
    : getBestAddress(profile?.address_obj, profile?.address) || "-";
  const fone = isLoading ? "..." : profile?.phone || "-";
  const email = isLoading ? "..." : profile?.email || "-";
  const pedidos = isLoading ? [] : orders ? orders.results : [];
  const mensagens = [
    { autor: "João", data: "18/05", texto: "Pedido ..." },
    { autor: "Gestora", data: "19/05", texto: "Ok..." },
  ];
  const comprovantes = [
    { arquivo: "boleto-124.pdf", data: "18/05" },
    { arquivo: "pix_123.jpg", data: "11/05" },
  ];

  const formik = useFormik<
    Partial<PUProfile> & { emailConfirm: string; passwordConfirm: string }
  >({
    initialValues: {
      name: profile?.name || "",
      email: profile?.email || "",
      emailConfirm: profile?.email || "",
      address_obj: profile?.address_obj || null,
      phone: profile?.phone || "",
      password: "",
      passwordConfirm: "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      name: Yup.string().required("Nome obrigatório"),
      email: Yup.string()
        .required("Email obrigatório")
        .test(
          "email-format",
          "Formato de email inválido. Use o formato: exemplo@dominio.com",
          function (value) {
            if (!value) return false;
            const error = validateEmail(value);
            return error === null;
          },
        ),
      emailConfirm: Yup.string()
        .oneOf([Yup.ref("email")], "Os emails não coincidem")
        .required("Confirme o email"),
      phone: Yup.string()
        .required("Telefone obrigatório")
        .test("phone-validation", "Telefone inválido", function (value) {
          if (!value) return false;
          const error = validatePhone(value);
          return error === null;
        }),
      password: Yup.string()
        .min(8, "A senha deve ter pelo menos 8 caracteres")
        .notRequired(),
      passwordConfirm: Yup.string().when("password", {
        is: (password: string) => password && password.length > 0,
        then: (schema) =>
          schema
            .required("Confirme a nova senha")
            .oneOf([Yup.ref("password")], "As senhas não coincidem"),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
    onSubmit: (values) => {
      const { emailConfirm, passwordConfirm, ...rest } = values;

      // Remove password se estiver vazio (não quer alterar)
      const updateData = { ...rest };
      if (!updateData.password || updateData.password.trim() === "") {
        delete updateData.password;
      }

      // Converte o telefone para E.164 antes de enviar
      if (updateData.phone) {
        try {
          updateData.phone = phoneToE164(updateData.phone);
        } catch (error) {
          // Se não conseguir converter, mantém o valor original
          // A validação do backend vai capturar o erro
        }
      }

      mutate(updateData, {
        onSuccess: () => {
          setPasswordChanged(!!updateData.password);
          setEditOpen(false);
          setEmailError(null);
          // Limpa os campos de senha após salvar
          formik.setFieldValue("password", "");
          formik.setFieldValue("passwordConfirm", "");
        },
        onError: (error: any) => {
          // Trata erros de validação de email
          if (error && typeof error === "object" && "email" in error) {
            const emailError = error.email;
            setEmailError(
              Array.isArray(emailError) ? emailError[0] : "Email inválido",
            );
          }
        },
      });
    },
  });

  const handleCloseDialog = () => {
    setEditOpen(false);
    setPasswordChanged(false);
    setEmailError(null);
    // Limpa os campos de senha
    formik.setFieldValue("password", "");
    formik.setFieldValue("passwordConfirm", "");
  };

  const handleOrderDetailsOpen = (order: Order) => {
    setSelectedOrder(order);
    setOrderDetailsOpen(true);
  };

  const handleOrderDetailsClose = () => {
    setOrderDetailsOpen(false);
    setSelectedOrder(null);
  };

  const handleAllOrdersOpen = () => {
    setAllOrdersOpen(true);
  };

  const handleAllOrdersClose = () => {
    setAllOrdersOpen(false);
  };

  const handleAllOrdersToDetails = (order: Order) => {
    setAllOrdersOpen(false);
    setSelectedOrder(order);
    setOrderDetailsOpen(true);
  };

  const creditTransactionsEnabled =
    process.env.REACT_APP_ENABLE_CREDIT_TRANSACTIONS === "true";
  const ordersEnabled = process.env.REACT_APP_ENABLE_ORDERS === "true";
  const messagesEnabled = process.env.REACT_APP_ENABLE_MESSAGES === "true";
  const receiptsEnabled = process.env.REACT_APP_ENABLE_RECEIPTS === "true";

  return (
    <Box sx={{ backgroundColor: "background.default", minHeight: "100vh" }}>
      <Box
        sx={{
          maxWidth: 1200,
          mb: 4,
          mx: "auto",
          mt: { xs: 4, md: 0 },
          p: { xs: 2, md: 4 },
          display: "flex",
          flexDirection: "column",
          gap: 6,
        }}
      >
        <StepTitle>Perfil</StepTitle>
        {/* Primeira linha: Informações Básicas e Crédito Disponível */}
        <Grid container spacing={4} sx={{ mb: 4 }} alignItems="stretch">
          <Grid
            item
            xs={12}
            md={creditTransactionsEnabled ? 6 : 12}
            sx={{ display: "flex", mb: { xs: 6, md: 0 } }}
          >
            <Paper
              elevation={3}
              sx={{
                p: 3,
                borderRadius: 3,
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                height: "100%",
              }}
            >
              <Typography
                variant="h6"
                color="secondary.main"
                sx={{ display: "flex", alignItems: "center", mb: 2 }}
              >
                <PersonIcon sx={{ mr: 1 }} /> Informações Básicas
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
                <Box
                  component="li"
                  sx={{ display: "flex", alignItems: "center", mb: 1 }}
                >
                  <PersonIcon sx={{ color: "primary.main", mr: 1 }} />
                  <Typography variant="subtitle1" fontWeight={600}>
                    {nome}
                  </Typography>
                </Box>
                <Box
                  component="li"
                  sx={{ display: "flex", alignItems: "center", mb: 1 }}
                >
                  <HomeIcon sx={{ color: "primary.main", mr: 1 }} />
                  <Typography variant="body1">{endereco}</Typography>
                </Box>
                <Box
                  component="li"
                  sx={{ display: "flex", alignItems: "center", mb: 1 }}
                >
                  <PhoneIcon sx={{ color: "primary.main", mr: 1 }} />
                  <Typography variant="body1">{fone}</Typography>
                </Box>
                <Box
                  component="li"
                  sx={{ display: "flex", alignItems: "center", mb: 1 }}
                >
                  <EmailIcon sx={{ color: "primary.main", mr: 1 }} />
                  <Typography variant="body1">{email}</Typography>
                </Box>
                <Box
                  component="li"
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <LockIcon sx={{ color: "primary.main", mr: 1 }} />
                  <Typography variant="body1">••••••••</Typography>
                </Box>
              </Box>
              <Box sx={{ textAlign: "right", mt: "auto" }}>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => setEditOpen(true)}
                >
                  Editar
                </Button>
              </Box>
            </Paper>
          </Grid>
          {process.env.REACT_APP_ENABLE_CREDIT_TRANSACTIONS === "true" && (
            <Grid item xs={12} md={6} sx={{ display: "flex" }}>
              <CreditTransactionsCard />
            </Grid>
          )}
        </Grid>
        {/* Segunda linha: Pedidos Recentes (full width) */}
        {ordersEnabled && (
          <Grid container spacing={0} sx={{ mb: 4 }}>
            <Grid item xs={12}>
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="h6" color="secondary.main">
                  📦 Pedidos Recentes
                </Typography>
                <Divider sx={{ my: 1 }} />

                {/* Exibição responsiva: Cards no mobile, Tabela no desktop */}
                {isMobile ? (
                  <Box sx={{ mt: 2 }}>
                    {pedidos.length === 0 ? (
                      <Box sx={{ textAlign: "center", py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          Nenhum pedido encontrado
                        </Typography>
                      </Box>
                    ) : (
                      pedidos.map((order: Order) => (
                        <OrderCard
                          key={order.id}
                          order={order}
                          onViewDetails={handleOrderDetailsOpen}
                        />
                      ))
                    )}
                  </Box>
                ) : (
                  <Box sx={{ overflowX: "auto" }}>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>#</TableCell>
                            <TableCell>Endereço</TableCell>
                            <TableCell>Itens</TableCell>
                            <TableCell>Data</TableCell>
                            <TableCell>Valor</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Ações</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {pedidos.map((p: Order) => (
                            <TableRow key={p.id}>
                              <TableCell>{p.id}</TableCell>
                              <TableCell>{p.address}</TableCell>
                              <TableCell>{p.items_count}</TableCell>
                              <TableCell>
                                {dayjs(p.created_at).format("DD/MM/YYYY")}
                              </TableCell>
                              <TableCell>R$ {p.total.toFixed(2)}</TableCell>
                              <TableCell>
                                {ORDER_STATUS_MAP[p.status]}
                              </TableCell>
                              <TableCell>
                                <Button
                                  size="small"
                                  onClick={() => handleOrderDetailsOpen(p)}
                                  variant="outlined"
                                >
                                  Ver detalhes
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                <Box sx={{ mt: 2, textAlign: "right" }}>
                  <Button variant="outlined" onClick={handleAllOrdersOpen}>
                    Ver todos os pedidos
                  </Button>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        )}
        {/* Terceira linha: Mensagens e Comprovantes */}
        {(messagesEnabled || receiptsEnabled) && (
          <Grid container spacing={4} sx={{ mb: 10 }} alignItems="stretch">
            {messagesEnabled && (
              <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    height: "100%",
                  }}
                >
                  <Typography variant="h6" color="secondary.main">
                    🗨️ Mensagens à Gestora
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <List dense>
                    {mensagens.map((m, i) => (
                      <ListItem key={i}>
                        <ListItemText
                          primary={`─ ${m.autor} ${m.data} "${m.texto}"`}
                        />
                      </ListItem>
                    ))}
                  </List>
                  <Box sx={{ display: "flex", gap: 1, mt: "auto" }}>
                    <TextField
                      size="small"
                      placeholder="Digite sua mensagem..."
                      fullWidth
                    />
                    <Button variant="contained">Enviar</Button>
                  </Box>
                </Paper>
              </Grid>
            )}
            {receiptsEnabled && (
              <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    height: "100%",
                  }}
                >
                  <Typography variant="h6" color="secondary.main">
                    📑 Comprovantes de Pagto
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ overflowX: "auto" }}>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Arquivo</TableCell>
                            <TableCell>Data</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {comprovantes.map((c, i) => (
                            <TableRow key={i}>
                              <TableCell>{c.arquivo}</TableCell>
                              <TableCell>{c.data}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                  <Box sx={{ textAlign: "right", mt: "auto" }}>
                    <Button variant="outlined">+ Enviar novo arquivo</Button>
                  </Box>
                </Paper>
              </Grid>
            )}
          </Grid>
        )}
      </Box>
      <Dialog open={editOpen} onClose={handleCloseDialog} fullWidth>
        <DialogTitle>Editar dados</DialogTitle>
        <form onSubmit={formik.handleSubmit}>
          <DialogContent>
            <TextField
              margin="dense"
              id="name"
              name="name"
              label="Nome"
              fullWidth
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />
            <TextField
              margin="dense"
              id="email"
              name="email"
              label="Email"
              fullWidth
              value={formik.values.email}
              onChange={(e) => {
                formik.handleChange(e);
                setEmailError(null); // Limpa erro quando o usuário digita
              }}
              error={
                (formik.touched.email && Boolean(formik.errors.email)) ||
                emailError !== null
              }
              helperText={
                (formik.touched.email && formik.errors.email) || emailError
              }
            />
            <TextField
              margin="dense"
              id="emailConfirm"
              name="emailConfirm"
              label="Confirme o Email"
              fullWidth
              value={formik.values.emailConfirm}
              onChange={formik.handleChange}
              error={
                formik.touched.emailConfirm &&
                Boolean(formik.errors.emailConfirm)
              }
              helperText={
                formik.touched.emailConfirm && formik.errors.emailConfirm
              }
            />
            <AddressFieldset
              address={formik.values.address_obj || null}
              onChange={(address_obj) =>
                formik.setFieldValue("address_obj", address_obj)
              }
            />
            <TextField
              margin="dense"
              id="phone"
              name="phone"
              label="Telefone"
              fullWidth
              value={formik.values.phone}
              onChange={formik.handleChange}
              error={formik.touched.phone && Boolean(formik.errors.phone)}
              helperText={formik.touched.phone && formik.errors.phone}
            />

            <Divider sx={{ my: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Segurança
              </Typography>
            </Divider>

            <TextField
              margin="dense"
              id="password"
              name="password"
              label="Nova Senha (opcional)"
              fullWidth
              type="password"
              placeholder="Deixe em branco para manter a senha atual"
              value={formik.values.password}
              onChange={formik.handleChange}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
            />
            <TextField
              margin="dense"
              id="passwordConfirm"
              name="passwordConfirm"
              label="Confirme a Nova Senha"
              fullWidth
              type="password"
              placeholder="Confirme a nova senha"
              value={formik.values.passwordConfirm}
              onChange={formik.handleChange}
              error={
                formik.touched.passwordConfirm &&
                Boolean(formik.errors.passwordConfirm)
              }
              helperText={
                formik.touched.passwordConfirm && formik.errors.passwordConfirm
              }
            />
            {isError && (
              <Alert severity="error">
                {(() => {
                  if (
                    typeof updateError === "string" &&
                    updateError.startsWith("<!DOCTYPE html>")
                  ) {
                    return "Algo deu errado! Não conseguimos salvar suas informações agora. Tente novamente ou fale com o suporte.";
                  }
                  if (
                    typeof updateError === "object" &&
                    updateError !== null &&
                    (updateError as any).detail ===
                      "Authentication credentials were not provided."
                  ) {
                    return "Você precisa estar logado para editar seus dados. Faça login e tente novamente!";
                  }
                  if (typeof updateError === "string") {
                    return updateError;
                  }
                  if (updateError && (updateError as any).message) {
                    return (updateError as any).message;
                  }
                  return "Algo deu errado! Não conseguimos salvar suas informações agora. Tente novamente ou fale com o suporte.";
                })()}
              </Alert>
            )}
            {isSuccess && (
              <Alert severity="success">
                {passwordChanged
                  ? "Dados atualizados com sucesso! Sua senha foi alterada."
                  : "Dados atualizados com sucesso!"}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={isSaving}>
              Cancelar
            </Button>
            <Button type="submit" variant="contained" disabled={isSaving}>
              Salvar
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {ordersEnabled && slug && (
        <>
          <OrderDetailsModal
            open={orderDetailsOpen}
            onClose={handleOrderDetailsClose}
            order={selectedOrder}
          />

          <AllOrdersModal
            open={allOrdersOpen}
            onClose={handleAllOrdersClose}
            onOrderDetails={handleAllOrdersToDetails}
            seedSlug={slug}
          />
        </>
      )}
    </Box>
  );
};

export default Perfil;
